import { getPrettyCarrierName } from "@redotech/redo-fulfillment-carrier-common/carrier-service-labels";
import type { ShipmentMessage } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import { useTranslation } from "@redotech/redo-i18n";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export type ShipmentMessagesByCarrier = Record<string, ShipmentMessage[]>;

export function groupShipmentMessages(
  records: Iterable<
    Readonly<Record<string, readonly ShipmentMessage[]>> | null | undefined
  >,
): ShipmentMessagesByCarrier {
  const messagesByCarrier: ShipmentMessagesByCarrier = {};
  const seen = new Set<string>();
  for (const record of records) {
    for (const messages of Object.values(record ?? {})) {
      for (const message of messages) {
        const carrier = message.carrier ?? "unknown";
        const messageKey = `${carrier}-${message.message}-${message.type}`;
        if (seen.has(messageKey) || message.ignored) {
          continue;
        }
        seen.add(messageKey);
        (messagesByCarrier[carrier] ??= []).push(message);
      }
    }
  }
  return messagesByCarrier;
}

export const ShipmentMessages = memo(function ShipmentMessages({
  description,
  messagesByCarrier,
}: {
  description: string;
  messagesByCarrier: ShipmentMessagesByCarrier;
}) {
  const { t } = useTranslation();
  return (
    <Flex dir="column" gap="lg">
      <Flex dir="column" gap="xs">
        <Text fontSize="sm" fontWeight="semibold" textColor="secondary">
          {t("Shipping messages")}
        </Text>
        <Text fontSize="xxs" textColor="tertiary">
          {description}
        </Text>
      </Flex>

      <Flex dir="column" gap="lg" pl="lg">
        {Object.entries(messagesByCarrier).map(([carrier, messages]) => {
          const visibleMessages = messages.filter(
            (message) => message.message?.length,
          );
          return visibleMessages.length > 0 ? (
            <Flex dir="column" gap="lg" key={carrier}>
              <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                {getPrettyCarrierName(carrier)}
              </Text>

              <Flex dir="column" gap="xxs" pl="lg">
                {visibleMessages.map((message, index) => (
                  <Flex align="flex-start" dir="row" gap="sm" key={index}>
                    <Text fontSize="xs" textColor="secondary">
                      •
                    </Text>
                    <Text fontSize="xs" textColor="secondary">
                      {message.message}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          ) : null;
        })}
      </Flex>
    </Flex>
  );
});
